
import * as React from "react";
import * as _ from "lodash";
import { IPatientAllergy, IPatientSystemReview } from "@curefit/ehr-common";
import {ISymptom} from "@curefit/care-common";
import {IPatientVitalResponse} from "../../models/IPatientVitalResponse";
import {EditViewDisplayMode} from "../common/EditViewCard";
import PatientVitalsSummaryBar from "../patient/summary-bar/PatientVitalsSummaryBar";
import PhysioChiefComplaintsEditViewCont from "../../containers/EditView/PhysioChiefComplaintsEditViewCont";
import ReviewOfSystemsEditViewCont from "../../containers/EditView/ReviewOfSystemsEditViewCont";
import IChiefComplaint from "../../models/IChiefComplaint";
import IEditedBy from "../../models/IEditedBy";
import ModelFactory from "../../models/ModelFactory";
import {AppMessages, SymptomTypes, DoctorType} from "../../utils/AppConstants";
import ErrorContainer from "../../containers/ErrorContainer";
import IStaticDataValue from "../../../../common/models/IStaticDataValue";
import CTA, {CTATheme} from "../common/CallToAction";

/**
 * Created by aditya on 25/02/18.
 */

export interface PhysioChiefComplaintsTabProps {
    appointmentId: number,
    editedBy: IEditedBy,
    patientId: number,
    patientAllergies?: IPatientAllergy[],
    patientVitals: IPatientVitalResponse[],
    patientChiefComplaints: ISymptom[],
    patientIllnesses: string,
    saveEditedChiefComplaints: (chiefComplaint: ISymptom[]) => void,
    saveEditedIllnesses: (illness: string) => void,
    savePatientSymptoms: (patientId: number, patientMedicalProfile: any) => any,
    fetchStaticSymptomsData: (input: string) => Promise<any>,
    nextTab: () => void,
    staticReviewSystemsData: IStaticDataValue[],
    saveReviewOfSystemsData: (data: IPatientSystemReview[]) => void,
    reviewOfSystemsData: IPatientSystemReview[],
    doctorType: string,
    suggestions: {[key: string]: any}
}

export interface PhysioChiefComplaintsTabState {
    error?: string,
    errorDetails?: string
}

class PhysioChiefComplaintsTab extends React.Component<PhysioChiefComplaintsTabProps, PhysioChiefComplaintsTabState> {
    private chiefComplaintsRef: PhysioChiefComplaintsEditViewCont;
    private reviewSystemsRef: ReviewOfSystemsEditViewCont;
    private isTherapist: boolean;
    private isPhysiotherapist: boolean;

    constructor(props: PhysioChiefComplaintsTabProps) {
        super(props);
        this.isTherapist = this.props.doctorType === DoctorType.MIND_THERAPIST;
        this.isPhysiotherapist = this.props.doctorType === DoctorType.PHYSIOTHERAPIST;
        this.state = {
            error: null,
            errorDetails: null
        }
    }

    componentWillUnmount() {
        this.saveEditedPatientComplaints();
    }

    saveEditedPatientComplaints() {
        let updatedChiefComplaint: IChiefComplaint = this.chiefComplaintsRef.getSaveObject();
        if (!_.isEmpty(updatedChiefComplaint.complaints) || !_.isEmpty(updatedChiefComplaint.illness)) {
            this.props.saveEditedChiefComplaints(updatedChiefComplaint.complaints.concat(updatedChiefComplaint.illness));
        }
        const hopi = this.chiefComplaintsRef.getHopisSaveObject();
        if (!_.isEmpty(hopi)) {
            this.props.saveEditedIllnesses(hopi);
        }
        if (this.isTherapist) {
            const updatedReviewOfSystemsData: IPatientSystemReview[] = this.reviewSystemsRef.getSaveObject();
            this.props.saveReviewOfSystemsData(updatedReviewOfSystemsData)
        }
    }

    savePatientComplaints = () => {
        const myInstance = this;
        if (!this.chiefComplaintsRef.isComplaintsFormDirty() && !this.chiefComplaintsRef.isIllnessesFormDirty()) {
            if (this.isTherapist) {
                const updatedReviewOfSystemsData: IPatientSystemReview[] = this.reviewSystemsRef.getSaveObject();
                if (updatedReviewOfSystemsData && updatedReviewOfSystemsData.length > 0) {
                    this.props.savePatientSymptoms(this.props.patientId, {patientSystemReviews: updatedReviewOfSystemsData}).then(function () {
                        console.log("Successfully Saved Medical Profile");
                        myInstance.props.nextTab();
                    }).catch((err: any) => {
                        this.setState({
                            error: AppMessages.VITALS_AND_ALLERGIES_SAVE_ERROR,
                            errorDetails: err.message
                        })
                    });
                }
            } else {
                myInstance.props.nextTab();
            }
        } else {
            this.saveEditedPatientComplaints();
            if (this.chiefComplaintsRef.isComplaintsFormDirty()) this.invalidItemError("Complaints")
            else if (this.chiefComplaintsRef.isIllnessesFormDirty()) this.invalidItemError("Illnesses")
        }
    }

    createChiefComplaintObjFromSymptoms = (): IChiefComplaint => {
        let chiefComplaint = ModelFactory.createNewChiefComplaint();
        let currentSymptoms = this.props.patientChiefComplaints;
        if ( !_.isEmpty(currentSymptoms) ) {
            chiefComplaint.illness = currentSymptoms.filter(item => item.type === SymptomTypes.ASSOCIATED)
            chiefComplaint.complaints = currentSymptoms.filter(item => item.type === SymptomTypes.CHIEF)
        } else {
            chiefComplaint.illness = [];
            chiefComplaint.complaints = [];
        }
        return chiefComplaint;
    }

    clearErrorMessage = () => {
        this.setState({
            error: null,
            errorDetails: null
        })
    }

    invalidItemError = (section: string) => {
        let errorDetails = section === "Complaints" ? this.chiefComplaintsRef.complaintsDirtyFormText() : this.chiefComplaintsRef.illnessesDirtyFormText();
        this.setState({
            error: AppMessages.CHIEF_COMPLAINTS_VALIDATION_ERROR,
            errorDetails: `Error in ${section}: ${errorDetails}`
        })
    }

    render() {
        let chiefComplaints = this.createChiefComplaintObjFromSymptoms();
        let patientHopi = this.props.patientIllnesses;
        let errorMessage = !_.isNull(this.state.error) ? this.state.error : AppMessages.CHIEF_COMPLAINTS_SAVE_ERROR;
        let errorDetails = !_.isNull(this.state.errorDetails) ? this.state.errorDetails : "";
        return (
            <div className="chief-complaints-tab-container">
                {
                    !_.isNull(this.state.error) &&
                    <ErrorContainer errorText={errorMessage} errorDetails={errorDetails} errorCode={0} closeError={this.clearErrorMessage}
                                    reportError={true} rollbarPayload={{message: errorMessage, errorDetails: errorDetails, request: this.chiefComplaintsRef.getSaveObject()}}/>
                }
                <div className={"sub-container"}>
                    <PatientVitalsSummaryBar patientVitals={this.props.patientVitals} patientAllergies={this.props.patientAllergies}/>
                </div>
                <div className={"sub-container"}>
                    <PhysioChiefComplaintsEditViewCont
                        ref={(physiochiefComplaintsDOM) => {this.chiefComplaintsRef = physiochiefComplaintsDOM}}
                        editedBy={this.props.editedBy}
                        patientId={this.props.patientId}
                        patientChiefComplaints={chiefComplaints}
                        patientHopis={patientHopi}
                        fetchStaticSymptoms={this.props.fetchStaticSymptomsData}
                        displayMode={EditViewDisplayMode.EDIT}
                        suggestions={this.props.suggestions}
                    />
                </div>
                {(this.isTherapist) &&
                    <div className={"sub-container review-of-systems"}>
                    <ReviewOfSystemsEditViewCont
                        editedBy={this.props.editedBy}
                        appointmentId={this.props.appointmentId}
                        patientId={this.props.patientId}
                        cardTitle={"Review of Systems"}
                        ref={(reviewSystems) => {this.reviewSystemsRef = reviewSystems}}
                        displayMode={EditViewDisplayMode.EDIT}
                        staticReviewSystemsData={this.props.staticReviewSystemsData}
                        reviewOfSystemsData={this.props.reviewOfSystemsData}
                    />
                </div>}
                <div className={"save-button collapsible-card"}>
                    <CTA innerText={"Save"} theme={CTATheme.PRIMARY} onClick={this.savePatientComplaints} customStyle={{display: "inline"}}/>
                </div>
            </div>
        );
    }
}

export default PhysioChiefComplaintsTab